<template>
  <v-container>
    <demand-fulfillment-summary/>
      
      <div class="demand-fulfillment__back">
        <v-btn depressed type="button"
          @click="$router.push({path:`/projects/${project.project_id}` + `/fulfillment/list`});" 
        >
          <v-icon>mdi-arrow-left</v-icon>
          <span>{{ $t('demandFulfillment.back') }}</span>
        </v-btn>
      </div>
      <v-row class="demand-fulfillment__name">
        <span class="data-table__details-view-headline">
          {{ $t('demandFulfillment.fulfillmentName') }} {{this.project.demand_fulfillment.name}}
        </span>
        <v-text-field
          class="search-text"
          v-model="search"
          hide-details
          :label="$t('search.text')"
          :append-icon="this.search.length == 0 ? 'mdi-magnify' : 'mdi-close'"
          @click:append="search = ''"
        />
      </v-row>

    <v-row no-gutters>
       <v-col cols="10">
           <v-data-table
            class="data-table__project"
            id="dataTableDemandFulfillment"
            ref="dataTableDemandFulfillment"
            :headers="headers"
            fixed-header
            height="55vh"
            :items="this.project.demand_fulfillment.nutrient_sources"
            :sort-by="this.nameProperty"
            default-sort="false"
            :options.sync="options"
            @click:row="itemDetails"
            hide-default-footer
            :item-key="`name_${this.$store.locale}`"
            group-by="category_id"
            :single-expand="false"
            single-select
            :search="search"
          >
          <template v-slot:group.header="{ group, items, isOpen, toggle }">
            <td>
              <v-btn
                @click="toggle"
                icon
                :ref="group"
                v-ripple="false"
                class="data-table__toggle-btn"
              >
                <v-icon v-if="isOpen">
                  mdi-chevron-down
                </v-icon>
                <v-icon v-else>
                  mdi-chevron-right
                </v-icon>
              </v-btn>
            </td>
            <td class="text-left">
              {{ getCategoryName(group) }}
            </td>
            <td></td>
            <td></td>
            <td></td>
            <td></td>
            <td></td>
            <td></td>
            <td></td>
            <td></td>
            <td>
                <v-tooltip top 
                  open-delay="800">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon
                      v-bind="attrs"
                      v-on="on"
                      >
                      <v-icon 
                        medium
                        class="data-table__add-output-btn" 
                        @click="addNewNutrientSource({ category_id: group }, toggle, isOpen)"
                      >
                        mdi-plus
                      </v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t('plantProduction.dataTable.tooltipAddToCategory') }}</span>
                </v-tooltip> 
            </td>
          </template>

          <!-- Table Body -->

          <template v-slot:item="val">
              <v-lazy :value="val.isExpanded" 
              tag="tr"
              transition=""
              >
                <td></td>
                <td class="text-left">
                  <v-tooltip v-if="hasDescription(val.item)" bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        :value="val.item['name_'+$i18n.locale] ? val.item['name_'+$i18n.locale] : getItemName(val.item)"
                        :readonly="val.item.id != undefined"
                        @blur="setInputName(val.item, $event.target.value)"
                        @keyup="setInputName(val.item, $event.target.value)"
                        hide-details
                        v-bind="attrs"
                        v-on="on"
                        @click="itemDetails(val.item)"
                      />
                    </template>
                    <span>{{val.item['description_'+$i18n.locale]}}</span>
                  </v-tooltip>
                  <v-text-field v-else
                      :value="val.item['name_'+$i18n.locale] ? val.item['name_'+$i18n.locale] : getItemName(val.item)"
                      :autofocus="focusOnEditButton(val.item)"
                      :key="val.item.added_uid"
                      @blur="setInputName(val.item, $event.target.value)"
                      @keyup="setInputName(val.item, $event.target.value)"
                      :readonly="val.item.id != undefined && val.item.category_id !== 'fertilizer_05'"
                      hide-details
                      @click="itemDetails(val.item)"
                  />
                </td>
                <td class="text-left">
                  <v-text-field
                    v-model.number="val.item.amount_input"
                    @keypress="preventUndesiredInput"
                    @keyup="handlerAmount(val.item.amount_input, val.item)"
                    v-on:input="setAmount(val.item, $event)"
                    @wheel="$event.currentTarget.blur()"
                    size="6"
                    hide-details
                    type="number"
                    @click="itemDetails(val.item)"
                  />
                </td>
                <td class="text-right">
                  <span @click="itemDetails(val.item)">{{getUnitName(val.item)}}</span>
                </td>
                <td class="text-right">
                  <span @click="itemDetails(val.item)" v-if="Number(parseFloat(val.item.N_t))">{{parseFloat(val.item.N_t).toFixed(1)}}</span>
                </td>
                <td class="text-right">
                  <span @click="itemDetails(val.item)" v-if="Number(parseFloat(val.item.N_kg_ha))">{{parseFloat(val.item.N_kg_ha).toFixed(1)}}</span>
                </td>
                <td class="text-right">
                   <span @click="itemDetails(val.item)" v-if="Number(parseFloat(val.item.P_t))">{{parseFloat(val.item.P_t).toFixed(1)}}</span>
                </td>
                <td class="text-right">
                  <span @click="itemDetails(val.item)" v-if="Number(parseFloat(val.item.P_kg_ha))">{{parseFloat(val.item.P_kg_ha).toFixed(1)}}</span>
                </td>
                <td class="text-right">
                  <span @click="itemDetails(val.item)" v-if="Number(parseFloat(val.item.K_t))">{{parseFloat(val.item.K_t).toFixed(1)}}</span>
                </td>
                <td class="text-right">
                  <span @click="itemDetails(val.item)" v-if="Number(parseFloat(val.item.K_kg_ha))">{{parseFloat(val.item.K_kg_ha).toFixed(1)}}</span>
                </td>
                <td>
                  <v-menu bottom left>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn icon v-bind="attrs" v-on="on">
                        <v-icon medium color="grey">
                          mdi-dots-horizontal
                        </v-icon>
                      </v-btn>
                    </template>
                    <v-list v-if="!val.item.id">
                      <v-list-item
                        v-for="(elem, i) in context_menu"
                        :key="i"
                        @click="elem.action(val.item)"
                      >
                      <v-list-item-title>{{ elem.title }}</v-list-item-title>
                    </v-list-item>
                    </v-list>
                    <v-list v-else>
                      <v-list-item @click="addNewDuplicatedNutrientSource(val.item)">
                        <v-list-item-title>{{ $t('contextMenu.duplicate') }}</v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                </td>  
              </v-lazy>
          </template> 
        </v-data-table>
      </v-col>

      <v-col cols="2">
        <v-card class="data-table__details-view plant-details" tile>
          <td v-if="selectedId !== -1">
            <span class="data-table__details-view-headline">{{ selected_output['name_'+$i18n.locale] }}</span>
            <p></p>
            <p>
              <v-text-field
                v-model="selected_output.N"
                :readonly="selected_output.id != undefined && selected_output.category_id !== 'fertilizer_05'"
                prefix="N:"
                :suffix="getUnitNameNutrientValues(selected_output)"
                type="number"
                v-on:input="setN(selected_output, $event)"
              ></v-text-field>
            </p>
            <p>
              <v-text-field
                v-model="selected_output.P"
                :readonly="selected_output.id != undefined && selected_output.category_id !== 'fertilizer_05'"
                prefix="P:"
                :suffix="getUnitNameNutrientValues(selected_output)"
                type="number"
                v-on:input="setP(selected_output, $event)"
              ></v-text-field>
            </p>
            <p>
              <v-text-field
                v-model="selected_output.K"
                :readonly="selected_output.id != undefined && selected_output.category_id !== 'fertilizer_05'"
                prefix="K:"
                :suffix="getUnitNameNutrientValues(selected_output)"
                type="number"
                v-on:input="setK(selected_output, $event)"
              ></v-text-field>
            </p>
          </td>
          <td v-else>
           <span class="data-table__details-view-headline">{{ $t('demandFulfillment.tableDetails.headline') }}</span>
            <p></p>
            <p>
              <span class="data-table__details-view-sub-headline">
              {{ $t('demandFulfillment.tableDetails.subtext') }}
              </span>
            </p>
            <p></p>
          </td>
        </v-card>
      </v-col>

    </v-row>
  </v-container>
</template>

<script>
import { mapState, mapGetters } from "vuex";
import store from "@/store/index";
import DemandFulfillmentSummary from '../components/DemandFulfillmentSummary.vue';
import { nameCheck } from "@/main.js";

export default {
  name: "DemandFulfillment",

  components: { DemandFulfillmentSummary },

  data() {
    return {
      context_menu: [
        {
          title: this.$i18n.t('contextMenu.duplicate'),
          action: function(item) {
            store.commit("addNewNutrientSource", item);
          },
        },
        
        {
          title: this.$i18n.t('contextMenu.remove'),
          action: function(item) {
            store.dispatch("removeNutrientSource", item);
          },
        },
      ],

      options: {
        itemsPerPage: -1,
      },
      headers: [
        {
          text: " ",
          sortable: false,
          width: 50
        },
        {
          text: this.$i18n.t('demandFulfillment.dataTable.culture'),
          sortable: false,
          value: "name_"+this.$i18n.locale,
          width: 350
        },
        { text: this.$i18n.t('demandFulfillment.dataTable.amount'), value: "amount_input", align: "right", width: 100 },

         { text: this.$i18n.t('animalProduction.dataTable.unit'), value: "unit_input", align: "right", cellClass: "right-input", width: 100 },

        { text: "N (t)", value: "N_t", align: "right", width: 70 },
        { text: "N (kg/ha)", value: "N_kg_ha", align: "right", width: 100 },
        
        { text: "P (t)", value: "P_t", align: "right", width: 70 },
        { text: "P (kg/ha)", value: "P_kg_ha", align: "right", width: 100 },

        { text: "K (t)", value: "K_t", align: "right", width: 70 },
        { text: "K (kg/ha)", value: "K_kg_ha", align: "right", width: 100 },
        
        { text: " ", value: "actions", sortable: false, align: "right", width: 50 },
      ],
      selected_output: {},
      selectedId: -1,
      search: ''
    };
  },

  computed: {
    ...mapGetters(['balance']),
    ...mapState(['project','categories', 'units']),

    nameProperty: function () {
      return 'name_'+this.locale
    },
  },

  mounted() {
    this.collapseTableGroups(this, "dataTableDemandFulfillment");
  },

  methods: {
    getUnitName(item) {
       let unitName = this.units.find((c) => c.id == item.unit_input);
       return unitName ? unitName['name_'+this.$i18n.locale] : "";
    },

    getUnitNameNutrientValues(item) {
       let unitName = this.units.find((c) => c.id == item.unit_id);
       return unitName ? unitName['name_'+this.$i18n.locale] : "";
    },

    hasDescription(item) {
      if (item['description_'+this.$i18n.locale] && item['description_'+this.$i18n.locale].length > 0) {
        return true;
      } else {
        return false;
      }
    },

    setN(item, val) {
      if (val && Number(val)) {
        let index = this.project.demand_fulfillment.nutrient_sources.indexOf(item);
        this.$store.commit('updateDemandValueN', {index,val})
      }
    },

    setP(item, val) {
      if (val && Number(val)) {
         let index = this.project.demand_fulfillment.nutrient_sources.indexOf(item);
        this.$store.commit('updateDemandValueP', {index,val})
      }
    },

    setK(item, val) {
      if (val && Number(val)) {
         let index = this.project.demand_fulfillment.nutrient_sources.indexOf(item);
        this.$store.commit('updateDemandValueK', {index,val})
      }
    },

    preventUndesiredInput($event) {
      var keyCode = ($event.keyCode ? $event.keyCode : $event.which);
      if (keyCode < 48 || keyCode > 57) {
        $event.preventDefault();
      }
    },
    
    addNewNutrientSource(item, toggle, isOpen) {
      if(!isOpen) {
        toggle()
      }
      this.$store.commit("addNewNutrientSource", item);
    },

    addNewDuplicatedNutrientSource(item) {
      this.$store.commit("addNewNutrientSource", item);
    },

    setInputName(item, newValue) {
      if (newValue === "") {
        let names = this.project.demand_fulfillment.nutrient_sources.map(out => out['name_'+this.$i18n.locale])
        let newName = this.getCategoryName(item.category_id)
        newName = nameCheck(newName, names)
        item.name_de = newName;
        item.name_en = newName
      } else {
        item.name_de = newValue;
        item.name_en = newValue
      }
    },

    getItemName(item) {
      if (!item['name_'+this.$i18n.locale]) {
        return "";
      }
    },

    focusOnEditButton(item) {
      if (item.added_uid && item['name_'+this.$i18n.locale] === '') {
        return true
      } else {
        return false
      }
    },

    handlerAmount(val, item) {
      if (val === "") {
        item.amount = 0;
      }
    },

    setAmount(item,val) {
      if (val) {
        let index = this.project.demand_fulfillment.nutrient_sources.indexOf(item);
        this.$store.commit('updateDemandValues',{index,val});
      } else {
        let index = this.project.demand_fulfillment.nutrient_sources.indexOf(item);
        let val = 0;
        this.$store.commit('updateDemandValues',{index,val});
      }
    },


    itemDetails(item) {
      this.selected_output = item;
      this.selectedId = item['name_'+this.$i18n.locale];
    },

    getCategoryName(category) {
      let cat = this.categories.find((c) => c.id == category);
      let result = cat ? cat['name_'+this.$i18n.locale] : "";
      return result;
    },

        /**
     * For a v-data-table using grouping this function collapses
     * all groups except for the first one.
     * @param vue a VueComponent instance (here just 'this')
     * @param ref the 'ref' value of a v-data-table
     */
    collapseTableGroups(vue, ref, expanded=false) {
      // lookup the table instance
      let table = vue.$refs[ref];
      // the $refs array contains the group labels and the table's ref name
      let refs = Object.keys(vue.$refs);
      // by setting the value on the table's 'openCache' to true/false, the groups
      // are either expanded or collapsed
      // must use $set to have this reactive
      refs.forEach(function(element) {
        table.$set(table.openCache, element, expanded);
      });
    },
  },

};
</script>
